<template>
  <footer
    class="footer-area"
    :style="`z-index: 100 !important; background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-footer)'}`"
  >
    <div class="container">
      <div class="footer-up">
        <div class="row">
          <!-- Desktop Layout -->
          <div class="col-lg-4 col-md-6 col-sm-12 tw-hidden md:tw-block">
            <NuxtImg :src="company.logo" class="xl:tw-w-78 xl:tw-max-w-78 mb-4 tw-max-w-48 sm:tw-max-w-60 lg:tw-w-60" />
            <div class="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-2">
              <p>{{ vars.companyTradingAsText }}</p>
              <p>{{ company.name }}</p>
              <p>Company Number: {{ vars.companyNumberText }}</p>
              <p>Registered Address: {{ vars.companyAddressText }}</p>
              <p>Redress Scheme: {{ vars.companyRedressSchemeText }}</p>
              <p>
                <a :href="vars.companyRedressSchemeLinkUrl" class="n-link" target="_blank" rel="noreferrer">
                  {{ vars.companyRedressSchemeLinkUrl }}
                </a>
              </p>

              <div class="mt-2 tw-flex tw-flex-row tw-items-center tw-gap-4">
                <a
                  v-for="(logo, index) in redress"
                  :key="`redressLogo-${index}`"
                  class="n-link"
                  :href="logo.link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img :src="logo.photo" :alt="logo.title" :title="logo.title" style="max-height: 42px" />
                </a>
              </div>
            </div>
          </div>

          <!-- Mobile Logo Section - Sadece mobilde görünür -->
          <div class="col-12 tw-block tw-text-center md:tw-hidden">
            <NuxtImg :src="company.logo" class="mb-4 tw-max-w-60" />
          </div>

          <!-- Contact Section -->
          <div class="col-lg-3 col-md-6 mt-3 tw-text-start">
            <h4>Contact</h4>
            <div class="tw-mt-12 tw-flex tw-flex-col tw-gap-5">
              <p>Contact Us</p>
              <nuxt-link href="#" class="tw-inline-block !tw-p-0">
                <p class="tw-text-start">{{ vars.companyPhoneText }}</p>
              </nuxt-link>
              <p class="tw-mb-6 !tw-p-0">
                <nuxt-link href="mailto:info@rochfordstokes.com" class="!tw-p-0"> info@rochfordstokes.com </nuxt-link>
              </p>
            </div>
          </div>

          <!-- Column 1 -->
          <div class="col-lg-3 col-md-6 col-sm-12 mt-3">
            <h4>{{ vars.columnTitle1Text }}</h4>
            <div class="tw-mt-12 tw-flex tw-flex-col tw-gap-5">
              <nuxt-link
                v-for="(column1, index) in columns1"
                :key="index"
                :to="column1.link"
                :external="true"
                class="n-link tw-flex tw-flex-row tw-items-center tw-gap-2"
              >
                <span v-if="column1.icon">
                  <NuxtImg :src="column1.icon" class="tw-w-4 tw-min-w-4" />
                </span>
                <p class="tw-break-words tw-duration-300" style="color: var(--c-text-footer)">
                  {{ column1.item }}
                </p>
              </nuxt-link>
            </div>
          </div>

          <!-- Column 2 -->
          <div class="col-lg-2 col-md-6 col-sm-12 mt-5 mt-md-3">
            <h4>{{ vars.columnTitle2Text }}</h4>
            <div class="tw-mt-12 tw-flex tw-flex-col tw-gap-5">
              <nuxt-link
                v-for="(column2, index) in columns2"
                :key="`column2-${index}`"
                :to="column2.link"
                :external="true"
                :target="column2.new_tab ? '_blank' : '_self'"
                class="n-link tw-group tw-flex tw-flex-row tw-items-center tw-gap-2"
              >
                <span v-if="column2.icon">
                  <NuxtImg :src="column2.icon" class="tw-w-4 tw-min-w-4" />
                </span>
                <p>{{ column2.item }}</p>
              </nuxt-link>
            </div>
          </div>

          <!-- Mobile Company Info & Redress Section - Sadece mobilde görünür -->
          <div class="col-12 tw-mt-8 tw-block md:tw-hidden">
            <div class="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-2">
              <p>{{ vars.companyTradingAsText }}</p>
              <p>{{ company.name }}</p>
              <p>Company Number: {{ vars.companyNumberText }}</p>
              <p>Registered Address: {{ vars.companyAddressText }}</p>
              <p>Redress Scheme: {{ vars.companyRedressSchemeText }}</p>
              <p>
                <a :href="vars.companyRedressSchemeLinkUrl" class="n-link" target="_blank" rel="noreferrer">
                  {{ vars.companyRedressSchemeLinkUrl }}
                </a>
              </p>

              <div class="mt-2 tw-flex tw-flex-row tw-items-center tw-gap-4">
                <a
                  v-for="(logo, index) in redress"
                  :key="`redressLogo-${index}`"
                  class="n-link"
                  :href="logo.link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img :src="logo.photo" :alt="logo.title" :title="logo.title" style="max-height: 42px" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>

  <div
    class="footer-bottom"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-footer)'}`"
  >
    <div class="container">
      <div class="row justify-content-center align-items-center justify-content-center">
        <div class="col-lg-6 col-md-6 col-sm-12">
          <p class="copyright-line" style="color: var(--c-text-footer)">
            © {{ new Date().getFullYear() }} {{ company.name }} All rights reserved.
          </p>
        </div>
        <div class="col-lg-6 col-md-6 col-xs-12 text-md-end">
          <p class="privacy" style="color: var(--c-text-footer)">
            Powered by
            <a href="https://www.iceberg-digital.co.uk/neuron/home" target="_blank" class="n-link"><b>Neuron</b> </a>
            | <a href="https://www.iceberg-digital.co.uk/" target="_blank" class="n-link"><b>Iceberg Digital</b></a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineNuxtComponent, navigateTo } from '#app';
import { mapState } from 'pinia';
import { ComponentMixin } from '~/mixins/component.mixin';
import { useWebsiteStore } from '~/units/website/store';

interface MenuItem {
  name: string;
  url: string;
  has_sub_menu: boolean;
  is_open_new_tab: boolean;
  sub_menus?: MenuItem[];
}

export default defineNuxtComponent({
  name: 'RochfordStokesFooter',
  mixins: [ComponentMixin],

  data() {
    return {
      menuVisible: false,
      isScrolled: false,
      openSubMenu: null as number | null,
    };
  },

  computed: {
    redress() {
      return this.groupedVariables.redress;
    },
    columns1() {
      return this.groupedVariables.columns1;
    },
    columns2() {
      return this.groupedVariables.columns2;
    },
    cards() {
      return this.groupedVariables.cards;
    },
    ...mapState(useWebsiteStore, ['company', 'headerMenuItems']),

    logo(): string {
      const currentCompany = this.company as any;
      const defaultLogo = currentCompany?.logo || '';
      // Hiçbir koşul olmaksızın sadece default logo dönsün.
      return defaultLogo;
    },

    footerMenuItems(): MenuItem[] {
      // headerMenuItems'ı footer için kullanıyorsan burayı düzenleyebilirsin
      return (this.headerMenuItems as MenuItem[]) || [];
    },
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    handleScroll(): void {
      this.isScrolled = window.scrollY > 50;
    },

    toggleSubMenu(index: number): void {
      // Açık olan alt menüyü tekrar tıklarsan kapatır, değilse açar
      this.openSubMenu = this.openSubMenu === index ? null : index;
    },

    handleMenuLinkClick(menuItem: MenuItem): void {
      if (!menuItem || menuItem.has_sub_menu || !menuItem.url) return;
      navigateTo(menuItem.url, {
        external: !!menuItem.is_open_new_tab,
        open: { target: menuItem.is_open_new_tab ? '_blank' : '_self' },
      });
    },
  },
});
</script>
